import React, { useState } from 'react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const ACCOUNTING_INTEGRATIONS = [
  { id: 'qbo', name: 'QuickBooks Online', status: 'disconnected', lastSync: null },
  { id: 'sage', name: 'Sage Intacct', status: 'connected', lastSync: '2026-03-30 08:00:12' },
  { id: 'xero', name: 'Xero', status: 'disconnected', lastSync: null },
  { id: 'netsuite', name: 'NetSuite', status: 'disconnected', lastSync: null },
];

const INITIAL_BANKS = [
  { id: 1, bank: 'National Bank of Canada', last4: '4821', status: 'active', autoImport: true },
  { id: 2, bank: 'RBC Royal Bank', last4: '7733', status: 'active', autoImport: false },
];

export default function IntegrationsSettings() {
  const t = useTheme();
  const savedIntegrations = JSON.parse(localStorage.getItem('finkloz_integrations_settings') || 'null');
  const [accountingSoftware, setAccountingSoftware] = useState(savedIntegrations?.accountingSoftware || ACCOUNTING_INTEGRATIONS);
  const [banks, setBanks] = useState(savedIntegrations?.banks || INITIAL_BANKS);
  const [payroll, setPayroll] = useState(savedIntegrations?.payroll || { provider: 'manual', status: 'disconnected' });
  const [docStorage, setDocStorage] = useState(savedIntegrations?.docStorage || { provider: 'internal', maxUpload: '25' });
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    localStorage.setItem('finkloz_integrations_settings', JSON.stringify({
      accountingSoftware, banks, payroll, docStorage,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleConnect = (id) => {
    setAccountingSoftware(prev => prev.map(s =>
      s.id === id
        ? { ...s, status: s.status === 'connected' ? 'disconnected' : 'connected', lastSync: s.status === 'disconnected' ? new Date().toISOString().slice(0, 19).replace('T', ' ') : null }
        : s
    ));
    markDirty();
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Integrations
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Connect accounting software, bank feeds, payroll, and document storage.
      </p>

      {/* Accounting Software */}
      <div style={sectionTitleStyle(t)}>Accounting Software</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
        {accountingSoftware.map(sw => (
          <div key={sw.id} style={{
            border: '1px solid ' + (sw.status === 'connected' ? green : t.border),
            borderRadius: 8,
            padding: 20,
            background: t.bgCard,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{
                  width: 40, height: 40, borderRadius: 8,
                  background: t.pillBg, marginBottom: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: t.textMuted, fontFamily: sans,
                }}>
                  {sw.name.charAt(0)}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>
                  {sw.name}
                </div>
              </div>
              <span style={{
                padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.3,
                background: sw.status === 'connected' ? greenLight : t.pillBg,
                color: sw.status === 'connected' ? green : t.textMuted,
              }}>
                {sw.status}
              </span>
            </div>

            {sw.lastSync && (
              <div style={{ fontSize: 10, color: t.textMuted, fontFamily: mono, marginBottom: 12 }}>
                Last sync: {sw.lastSync}
              </div>
            )}

            <button
              onClick={() => handleConnect(sw.id)}
              style={{
                padding: '6px 16px',
                border: sw.status === 'connected' ? '1px solid ' + t.border : 'none',
                borderRadius: 6,
                background: sw.status === 'connected' ? 'transparent' : green,
                color: sw.status === 'connected' ? t.textSecondary : '#fff',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: sans,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              {sw.status === 'connected' ? 'Disconnect' : 'Connect'}
            </button>
          </div>
        ))}
      </div>

      {/* Bank Feeds */}
      <div style={sectionTitleStyle(t)}>Bank Feeds</div>

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['Bank', 'Account', 'Status', 'Auto-Import'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px',
                  textAlign: 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {banks.map(b => (
              <tr key={b.id} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {b.bank}
                </td>
                <td style={{ padding: '10px 14px', color: t.textSecondary, fontFamily: mono, fontSize: 11 }}>
                  ****{b.last4}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    background: greenLight, color: green,
                  }}>
                    {b.status}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div
                    onClick={() => {
                      setBanks(prev => prev.map(bk => bk.id === b.id ? { ...bk, autoImport: !bk.autoImport } : bk));
                      markDirty();
                    }}
                    style={toggleStyle(b.autoImport, t)}
                  >
                    <div style={toggleKnob(b.autoImport)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payroll */}
      <div style={sectionTitleStyle(t)}>Payroll</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
        <div>
          <label style={labelStyle(t)}>Provider</label>
          <select
            value={payroll.provider}
            onChange={e => { setPayroll(p => ({ ...p, provider: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          >
            <option value="manual">Manual</option>
            <option value="wagepoint">Wagepoint</option>
            <option value="adp">ADP</option>
            <option value="ceridian">Ceridian</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
          <span style={{
            padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: 0.3,
            background: payroll.provider !== 'manual' ? greenLight : t.pillBg,
            color: payroll.provider !== 'manual' ? green : t.textMuted,
          }}>
            {payroll.provider !== 'manual' ? 'connected' : 'manual entry'}
          </span>
        </div>
      </div>

      <div style={{ marginBottom: 32 }} />

      {/* Document Storage */}
      <div style={sectionTitleStyle(t)}>Document Storage</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Storage Provider</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { value: 'internal', label: 'Internal (FinKloz storage)' },
            { value: 'google-drive', label: 'Google Drive' },
            { value: 'sharepoint', label: 'SharePoint' },
          ].map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="docStorage"
                value={opt.value}
                checked={docStorage.provider === opt.value}
                onChange={() => { setDocStorage(p => ({ ...p, provider: opt.value })); markDirty(); }}
                style={{ accentColor: green }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 32 }}>
        <label style={labelStyle(t)}>Max Upload Size</label>
        <select
          value={docStorage.maxUpload}
          onChange={e => { setDocStorage(p => ({ ...p, maxUpload: e.target.value })); markDirty(); }}
          style={{ ...inputStyle(t), maxWidth: 200 }}
        >
          <option value="10">10 MB</option>
          <option value="25">25 MB</option>
          <option value="50">50 MB</option>
          <option value="100">100 MB</option>
        </select>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
